import { useEffect, useState, type ReactNode } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import DashboardIcon from '@mui/icons-material/SpaceDashboard'
import PeopleIcon from '@mui/icons-material/People'
import InventoryIcon from '@mui/icons-material/Inventory2'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'
import SavingsIcon from '@mui/icons-material/SavingsOutlined'
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined'
import ReceiptIcon from '@mui/icons-material/ReceiptLong'
import EventIcon from '@mui/icons-material/CalendarMonth'
import TaskIcon from '@mui/icons-material/CheckCircleOutline'
import CampaignIcon from '@mui/icons-material/CampaignOutlined'
import BarChartIcon from '@mui/icons-material/BarChart'
import GroupIcon from '@mui/icons-material/GroupOutlined'
import SettingsIcon from '@mui/icons-material/Settings'
import SearchIcon from '@mui/icons-material/Search'
import MenuIcon from '@mui/icons-material/Menu'
import PersonIcon from '@mui/icons-material/PersonOutline'
import SwapIcon from '@mui/icons-material/SwapHorizontalCircleOutlined'
import LogoutIcon from '@mui/icons-material/LogoutOutlined'
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined'
import LightModeIcon from '@mui/icons-material/LightModeOutlined'
import { SyncBadge, type SyncState } from '../../ui/SyncBadge.js'
import { useConflicts, useSyncStatus } from '../../data/hooks.js'
import { ConflictDialog } from '../../features/sync/ConflictDialog.js'
import { useSessionStore } from '../../stores/sessionStore.js'
import { CommandPalette } from '../../ui/CommandPalette.js'
import { Marque, PiedDePage } from '../../ui/Marque.js'
import { fondPastille } from '../../ui/pastille.js'
import { resolveThemeMode, useUiStore } from '../../stores/uiStore.js'

const SIDEBAR_WIDTH = 240

/**
 * État du moteur → pastille (W-04).
 *
 * Un conflit prime sur tout le reste : c'est le seul état qui réclame une
 * action de l'utilisateur. « Hors ligne » n'est pas une panne mais un mode de
 * fonctionnement normal, et reste donc en dessous.
 */
function badgeFrom(phase: string, conflicts: number, bloquees: number): SyncState {
  if (conflicts > 0) return 'conflict'
  // ⚠️ AVANT l'état « synchronisé », et même avant « hors ligne ». Une mutation
  // refusée par le serveur ne repartira jamais d'elle-même : annoncer
  // « Synchronisé » reviendrait à certifier que la saisie est enregistrée
  // alors qu'elle n'existe que sur cet appareil.
  if (bloquees > 0) return 'blocked'
  // `error` — serveur injoignable — se présente comme « hors ligne » : du point
  // de vue de l'utilisateur c'est la même situation, et afficher
  // « Synchronisation… » pendant qu'aucun échange n'aboutit serait un mensonge.
  if (phase === 'offline' || phase === 'error') return 'offline'
  if (phase === 'idle') return 'synced'
  return 'syncing'
}
const SIDEBAR_COLLAPSED = 72

const NAV = [
  { label: 'Tableau de bord', icon: <DashboardIcon />, path: 'dashboard', module: 'dashboard' },
  { label: 'Clients', icon: <PeopleIcon />, path: 'clients', module: 'clients' },
  { label: 'Catalogue', icon: <InventoryIcon />, path: 'catalogue', module: 'catalogue' },
  { label: 'Ventes', icon: <PointOfSaleIcon />, path: 'ventes', module: 'ventes' },
  /**
   * ⚠️ `tiroir` et non `caisse` : `/s/:slug/caisse` est déjà l'écran
   * d'encaissement, qui vit hors de la coquille. Deux entrées de menu pour
   * deux chemins voisins auraient surligné la mauvaise au retour de la caisse,
   * le repérage se faisant par préfixe d'URL.
   */
  { label: 'Caisse', icon: <SavingsIcon />, path: 'tiroir', module: 'ventes' },
  {
    label: 'Dépenses courantes',
    icon: <PaymentsIcon />,
    path: 'depenses',
    module: 'depenses',
  },
  { label: 'Facturation', icon: <ReceiptIcon />, path: 'facturation', module: 'facturation' },
  { label: 'Agenda', icon: <EventIcon />, path: 'agenda', module: 'agenda' },
  { label: 'Tâches', icon: <TaskIcon />, path: 'taches', module: 'taches' },
  {
    label: 'Campagnes marketing',
    icon: <CampaignIcon />,
    path: 'campagnes',
    module: 'campagnes',
  },
  { label: 'Rapports', icon: <BarChartIcon />, path: 'rapports', module: 'rapports' },
  { label: 'Utilisateurs', icon: <GroupIcon />, path: 'utilisateurs', module: 'utilisateurs' },
  { label: 'Paramètres', icon: <SettingsIcon />, path: 'parametres', module: 'parametres' },
] as const

/**
 * Charpente applicative (W-04) : entête + menu latéral + contenu.
 *
 * Le menu se réduit en icônes sur bureau et devient un tiroir sur mobile —
 * le contenu conserve la même largeur utile dans les deux cas.
 */
export function AppLayout({ children }: { children: ReactNode }): JSX.Element {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { sidebarCollapsed, toggleSidebar, themePreference, setThemePreference } = useUiStore()
  const [mobileOpen, setMobileOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { slug } = useParams<{ slug: string }>()
  const store = useSessionStore((st) => (slug ? st.storeBySlug(slug) : undefined))
  const user = useSessionStore((st) => st.user)
  const logout = useSessionStore((st) => st.logout)
  const [compteAnchor, setCompteAnchor] = useState<HTMLElement | null>(null)
  const nomComplet = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || (user?.email ?? '')
  const initiales = nomComplet.slice(0, 2).toUpperCase() || '?'
  /**
   * Le titre de l'onglet porte le NOM DE LA BOUTIQUE.
   *
   * ⭐ Un gérant qui travaille sur deux enseignes ouvre deux onglets : « IOBIZ »
   * des deux côtés ne lui dit pas lequel est lequel. Le nom, si.
   */
  useEffect(() => {
    if (store?.name) document.title = store.name
  }, [store?.name])

  const sync = useSyncStatus()
  const conflicts = useConflicts(store?.storeId)
  const badgeState = badgeFrom(sync.phase, conflicts.length, sync.bloquees)
  const [arbitrating, setArbitrating] = useState(false)

  // L'élément actif se déduit de l'URL, jamais d'un état local : sinon le
  // bouton Précédent du navigateur désynchronise le menu de la page affichée.
  const active = NAV.findIndex((item) =>
    location.pathname.startsWith(`/s/${slug}/${item.path}`),
  )

  const mode = resolveThemeMode(themePreference)
  const collapsed = !isMobile && sidebarCollapsed
  const width = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH

  const nav = (
    <List sx={{ pt: 2 }}>
      {NAV.map((item, index) => {
        /**
         * ⭐ Un module désactivé est MASQUÉ.
         *
         * Il restait grisé, pour dire « ça existe, mais pas ici ». À l'usage
         * c'est le contraire qui se produit : le commerçant voit chaque jour
         * des entrées qu'il ne peut pas ouvrir, et son menu paraît à moitié en
         * panne. Ce qui est coupé disparaît.
         *
         * Un code ABSENT vaut actif : les boutiques créées avant ce réglage ne
         * doivent pas se réveiller amputées de leur menu.
         */
        const actif = store?.modules?.[item.module] !== false
        if (!actif) return null
        return (
        <Tooltip
          key={item.label}
          title={collapsed ? item.label : ''}
          placement="right"
        >
          <span>
          <ListItemButton
            selected={index === active}
            onClick={() => {
              navigate(`/s/${slug}/${item.path}`)
              setMobileOpen(false)
            }}
            sx={{
              mx: 2,
              mb: 1,
              borderRadius: 2,
              minHeight: 44, // cible tactile ≥ 44 px (§7.12)
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
          >
            <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36 }}>{item.icon}</ListItemIcon>
            {!collapsed && <ListItemText primary={item.label} />}
          </ListItemButton>
          </span>
        </Tooltip>
        )
      })}
    </List>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <CommandPalette />

      {/* Un seul conflit à la fois : les traiter en lot transformerait
          l'arbitrage en corvée administrative. Le suivant s'ouvre dès que
          celui-ci est réglé. */}
      {store && arbitrating && (
        <ConflictDialog
          storeId={store.storeId}
          conflict={conflicts[0] ?? null}
          onClose={() => setArbitrating(conflicts.length > 1)}
        />
      )}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ gap: 3, minHeight: 56 }}>
          {/* ⚠️ Le repli est PERSISTANT, et c'est ce qui en fait un piège :
              un clic de trop réduit le menu à ses icônes, et il le reste aux
              chargements suivants. Sans infobulle, l'écran se lit comme des
              libellés disparus plutôt que comme un menu plié — ça a été
              rapporté comme une panne. L'infobulle dit donc l'action à venir,
              pas l'état courant. */}
          <Tooltip
            title={
              isMobile
                ? 'Ouvrir le menu'
                : sidebarCollapsed
                  ? 'Déployer le menu'
                  : 'Réduire le menu'
            }
          >
            <IconButton
              edge="start"
              aria-label={
                isMobile
                  ? 'Ouvrir le menu'
                  : sidebarCollapsed
                    ? 'Déployer le menu'
                    : 'Réduire le menu'
              }
              onClick={() => (isMobile ? setMobileOpen(!mobileOpen) : toggleSidebar())}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>

          {/* ⭐ L'ENTÊTE porte la boutique, pas la marque IOBIZ. Sur un compte
              qui en gère plusieurs, « où suis-je » est la question permanente ;
              « quel logiciel j'utilise » ne se pose qu'une fois. La marque
              reste présente en pied de page, où elle signe sans occuper la
              place la plus visible de l'écran. */}
          {store ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
              <Avatar
                src={store.logoUrl ?? undefined}
                variant="rounded"
                sx={{
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  fontSize: 13,
                  ...fondPastille(store.logoUrl),
                }}
              >
                {store.name.slice(0, 2).toUpperCase()}
              </Avatar>
              {/* ⚠️ Le slug technique (« /s/dstress ») a disparu de l'entête :
                  c'est une adresse d'URL, pas une information qu'un
                  commerçant a besoin de lire en permanence sous le nom de sa
                  boutique. */}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="div"
                  sx={{
                    fontWeight: 700,
                    fontSize: 17,
                    letterSpacing: '-.02em',
                    lineHeight: 1.1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {store.name}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Marque mention="par eduqor" />
          )}

          <Paper
            variant="outlined"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              maxWidth: 520,
              px: 3,
              py: 1,
              borderRadius: 2,
              bgcolor: 'background.default',
            }}
          >
            <SearchIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 2 }} />
            <InputBase
              placeholder="Rechercher…"
              inputProps={{ 'aria-label': 'Recherche globale' }}
              sx={{ flex: 1, fontSize: 14 }}
            />
            <Typography variant="caption" sx={{ color: 'text.disabled', ml: 2 }}>
              Ctrl K
            </Typography>
          </Paper>

          <Box sx={{ flex: 1 }} />

          {/* Cliquable seulement quand il y a matière : une pastille qui
              n'ouvre rien apprend à l'utilisateur à ne plus cliquer dessus. */}
          <Box
            onClick={conflicts.length > 0 ? () => setArbitrating(true) : undefined}
            sx={{ cursor: conflicts.length > 0 ? 'pointer' : 'default' }}
            role={conflicts.length > 0 ? 'button' : undefined}
            tabIndex={conflicts.length > 0 ? 0 : undefined}
            onKeyDown={(e) => {
              if (conflicts.length > 0 && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault()
                setArbitrating(true)
              }
            }}
          >
            <SyncBadge
              state={badgeState}
              pending={conflicts.length || sync.bloquees || sync.pending}
            />
          </Box>

          <Tooltip title={mode === 'dark' ? 'Thème clair' : 'Thème sombre'}>
            <IconButton
              aria-label="Changer de thème"
              onClick={() => setThemePreference(mode === 'dark' ? 'light' : 'dark')}
            >
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          {/* ⚠️ Ces initiales étaient CODÉES EN DUR (« MD ») et l'avatar
              n'était pas cliquable : l'application n'offrait aucun moyen de se
              déconnecter, de changer de boutique ou de voir son compte. */}
          <Tooltip title="Mon compte">
            <IconButton
              aria-label="Mon compte"
              onClick={(e) => setCompteAnchor(e.currentTarget)}
              sx={{ p: 0, ml: 1 }}
            >
              <Avatar
                src={user?.avatarUrl ?? undefined}
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: 13,
                  // `cover` : un portrait est déjà recadré en 3:4, il doit
                  // remplir la pastille ronde plutôt que d'y flotter.
                  ...fondPastille(user?.avatarUrl, 'cover'),
                }}
              >
                {initiales}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={compteAnchor}
            open={Boolean(compteAnchor)}
            onClose={() => setCompteAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ px: 4, py: 2, minWidth: 220 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {nomComplet}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                setCompteAnchor(null)
                navigate(`/s/${slug}/mon-compte`)
              }}
            >
              <PersonIcon sx={{ fontSize: 18, mr: 2 }} />
              Mon compte
            </MenuItem>
            <MenuItem
              onClick={() => {
                setCompteAnchor(null)
                navigate('/s')
              }}
            >
              <SwapIcon sx={{ fontSize: 18, mr: 2 }} />
              Changer de boutique
            </MenuItem>
            {user?.isPlatformAdmin && (
              <MenuItem
                onClick={() => {
                  setCompteAnchor(null)
                  navigate('/admin/utilisateurs')
                }}
              >
                <GroupIcon sx={{ fontSize: 18, mr: 2 }} />
                Console plateforme
              </MenuItem>
            )}
            <Divider />
            <MenuItem
              sx={{ color: 'error.main' }}
              onClick={() => {
                setCompteAnchor(null)
                void logout()
              }}
            >
              <LogoutIcon sx={{ fontSize: 18, mr: 2 }} />
              Se déconnecter
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isMobile ? 0 : width,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isMobile ? SIDEBAR_WIDTH : width,
            boxSizing: 'border-box',
            borderRight: 1,
            borderColor: 'divider',
            transition: theme.transitions.create('width', { duration: 180 }),
            overflowX: 'hidden',
          },
        }}
      >
        <Toolbar sx={{ minHeight: 56 }} />

        {nav}
      </Drawer>

      {/* Colonne : le contenu pousse, le pied reste en bas — y compris sur une
          page courte, où un pied « collé au texte » a l'air d'un accident. */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <Toolbar sx={{ minHeight: 56 }} />
        <Box sx={{ flex: 1, p: { xs: 4, md: 6 } }}>{children}</Box>
        <PiedDePage />
      </Box>
    </Box>
  )
}
